import React, { useState, useEffect } from "react";

const PRIORITY_COLORS = {
  critical: "var(--red-500)",
  high: "var(--orange-500)",
  medium: "var(--amber-500)",
  low: "var(--blue-500)",
};

const PRIORITY_LABELS = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };

const MODULE_LABELS = {
  subdomains: "Subdomains",
  ports: "Ports",
  dns: "DNS",
  whois: "WHOIS",
  reversedns: "Reverse DNS",
  certificates: "Certificates",
  brand: "Brand protection",
};

const CHANGE_LABELS = { added: "New", changed: "Changed", removed: "Removed" };

function timeAgo(value) {
  const diff = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(diff) || diff < 0) return "";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/**
 * Dashboard card: latest attack surface changes (new subdomain, port, DNS or WHOIS change).
 */
const RecentChangesCard = () => {
  const [events, setEvents] = useState(null);
  const [total, setTotal] = useState(0);
  const [priority, setPriority] = useState("all");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const apiBase = process.env.REACT_APP_DEFAULTPATH || "http://localhost:1337/";
    const query = priority === "all" ? "" : `&priority=${priority}`;

    fetch(`${apiBase}changes?limit=8${query}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        if (res && Array.isArray(res.data)) {
          setEvents(res.data);
          setTotal(res.total || 0);
        }
      })
      .catch(() => {});
  }, [priority]);

  if (!events) return null;

  return (
    <div className="asm-card asm-changes-card">
      <div className="asm-card-header asm-changes-header">
        <span>Recent Changes</span>
        <select
          className="asm-changes-filter"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          aria-label="Filter changes by priority"
        >
          <option value="all">All priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {events.length === 0 ? (
        <div className="asm-changes-empty">
          No changes detected yet. The first scan only records a baseline; changes show up from the next scan.
        </div>
      ) : (
        <>
          <ul className="asm-changes-list">
            {events.map((e) => (
              <li className="asm-changes-item" key={e._id}>
                <span
                  className="asm-changes-badge"
                  style={{ color: PRIORITY_COLORS[e.priority], borderColor: PRIORITY_COLORS[e.priority] }}
                >
                  {PRIORITY_LABELS[e.priority] || e.priority}
                </span>
                <div className="asm-changes-main">
                  <div className="asm-changes-title">{e.title}</div>
                  <div className="asm-changes-meta">
                    {MODULE_LABELS[e.module] || e.module} · {CHANGE_LABELS[e.changeType] || e.changeType} ·{" "}
                    {timeAgo(e.detectedAt)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {total > events.length && (
            <div className="asm-changes-footer">Showing latest {events.length} of {total} changes</div>
          )}
        </>
      )}
    </div>
  );
};

export default RecentChangesCard;
